import { isArray, isDefined, SmartQueue, dashCase } from '../utils/index.js';
import type { Base, BaseConfig, BaseConstructor } from './index.js';
import { features } from './features.js';
import { useMutation } from '../services/MutationService.js';
import { getInstanceFromElement } from '../helpers/getInstanceFromElement.js';

let queue: SmartQueue;

/**
 * Add a task to the main queue.
 */
export function addToQueue(fn: () => unknown) {
  if (features.get('blocking')) {
    fn();
    return;
  }

  if (!queue) {
    queue = new SmartQueue();
  }

  return queue.add(fn);
}

const selectors = new Map();

// Separator used for multi-component declaration in `data-component` attributeS.
const separator = ' ';

/**
 * Get the selector for a given component.
 */
export function getSelector(nameOrSelector: string): string {
  const { component } = features.get('attributes');
  const prefix = features.get('prefix');
  const key = prefix + component + nameOrSelector;

  if (!selectors.has(key)) {
    const parts = [
      // Tag select
      `${prefix}-${dashCase(nameOrSelector)}`,
      // Single selector
      `[${component}="${nameOrSelector}"]`,
      // Selector in the middle of a list of selectors
      `[${component}*="${separator}${nameOrSelector}${separator}"]`,
      // Selector at the end of a list of selectors
      `[${component}$="${separator}${nameOrSelector}"]`,
      // Selector at the beginning of a list of selectors
      `[${component}^="${nameOrSelector}${separator}"]`,
    ];
    selectors.set(key, parts.join(','));
  }

  return selectors.get(key);
}

/**
 * Get a list of elements based on the name of a component.
 *
 * @param {string} nameOrSelector
 *   The name or selector to used for this component.
 * @param {HTMLElement|Document} element
 *   The root element on which to query the selector, defaults to `document`.
 * @return {Array<HTMLElement>}
 *   A list of elements on which the component should be mounted.
 */
export function getComponentElements(
  nameOrSelector: string,
  element: HTMLElement | Document = document,
): HTMLElement[] {
  const selector = getSelector(nameOrSelector);
  let elements = [];

  try {
    elements = Array.from(element.querySelectorAll(selector));
    // eslint-disable-next-line no-empty
  } catch {}

  // If no child component found with the default selector, and if the selector does not
  // start with an uppercase letter, matching a component's name, try a classic DOM selector.
  if (elements.length === 0 && nameOrSelector[0] === nameOrSelector[0].toLowerCase()) {
    elements = Array.from(element.querySelectorAll(nameOrSelector));
  }

  return elements;
}

/**
 * Test if an event is defined in the given config.
 */
export function eventIsDefinedInConfig(event: string, config: BaseConfig): boolean {
  return isArray(config.emits) && config.emits.includes(event);
}

/**
 * Test if an event can be used on the given element.
 */
export function eventIsNative(event: string, element: HTMLElement): boolean {
  return isDefined(element[`on${event}`]);
}

/**
 * Get the target of a given event.
 */
export function getEventTarget(instance: Base, event: string, config: BaseConfig): Base['$el'] {
  if (eventIsDefinedInConfig(event, config) || eventIsNative(event, instance.$el)) {
    return instance.$el;
  }
}

/**
 * Get the global instance storage.
 * We attach it to `globalThis` so it can be shared between different
 * instances of the framework.
 */
function getInstancesStorage(): Set<Base> {
  return (globalThis.__JS_TOOLKIT_INSTANCES__ ??= new Set<Base>());
}

/**
 * Get the global elements storage.
 * It will hold reference to all elements with at least one component attached to it.
 */
function getElementsStorage(): Set<HTMLElement & { __base__: Map<string, Base>}> {
  return (globalThis.__JS_TOOLKIT_ELEMENTS__ ??= new Set<HTMLElement & { __base__: Map<string, Base>}>());
}

/**
 * Get all mounted instances or the ones from a given component.
 * @link https://js-toolkit.studiometa.dev/api/helpers/getInstances.html
 */
export function getInstances(): Set<Base>;
export function getInstances<T extends BaseConstructor = BaseConstructor>(
  ctor: T,
): Set<InstanceType<T>>;
export function getInstances<T extends BaseConstructor = BaseConstructor>(
  ctor?: T,
): Set<InstanceType<T>> | Set<Base> {
  if (isDefined(ctor)) {
    const filteredInstances = new Set<InstanceType<T>>();
    for (const instance of getInstancesStorage()) {
      if (instance instanceof ctor) {
        filteredInstances.add(instance as InstanceType<T>);
      }
    }
    return filteredInstances;
  } else {
    return new Set(getInstancesStorage());
  }
}

export function getElements() {
  return new Set(getElementsStorage());
}

export function addInstance(instance: Base) {
  getInstancesStorage().add(instance);
  getElementsStorage().add(instance.$el as HTMLElement & { __base__: Map<string, Base>});
}

export function deleteInstance(instance: Base) {
  getInstancesStorage().delete(instance);
  getElementsStorage().delete(instance.$el as HTMLElement & { __base__: Map<string, Base>});
}

const registryKey = '__JS_TOOLKIT_REGISTRY__';

function registry() {
  return (globalThis[registryKey] ??= new Map<string, BaseConstructor>());
}

function mutationCallback() {
  addToQueue(() => {
    for (const [nameOrSelector, ctor] of registry()) {
      for (const el of getComponentElements(nameOrSelector)) {
        if (!getInstanceFromElement(el, ctor)) {
          new ctor(el).$mount();
        }
      }
    }
  });

  addToQueue(() => {
    for (const instance of getInstances()) {
      if (!instance.$el.isConnected) {
        instance.$terminate();
      }
    }
  });
}

export function addToRegistry(nameOrSelector: string, ctor: BaseConstructor) {
  if (registry().has(nameOrSelector)) {
    return;
  }

  registry().set(nameOrSelector, ctor);

  // Add children components to the registry
  if (ctor.config?.components) {
    for (const [selector, childConstructor] of Object.entries(ctor.config?.components)) {
      if ('$isBase' in childConstructor && childConstructor.$isBase) {
        addToRegistry(selector, childConstructor);
      }
    }
  }

  const mutation = useMutation(document, { childList: true, subtree: true });
  if (!mutation.has(registryKey)) {
    mutation.add(registryKey, mutationCallback);
  }
}

/**
 * Find the closest instance from the given element in the given set of Base instances.
 */
export function findClosestInstance<T extends Base = Base>(element: HTMLElement, instances: Set<T>): T {
  let closest = null;
  let minDepth = Infinity;

  for (const instance of instances) {
    let depth = 0;
    let el = element;
    while (el && el !== instance.$el) {
      depth++;
      el = el.parentElement;
    }

    if (depth < minDepth) {
      minDepth = depth;
      closest = instance;
    }
  }

  return closest;
}
